import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useContext, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';
import { createUseStyles, Chip, SelectWrapper } from '@Sage/hoi-poi-ui';
import { CopilotContext, CopilotProvider, CopilotConsumer } from "../Context";
import Chat from "./components/Chat";
import ChatToolbar from "./components/ChatToolbar";
import { getCopilotStreamAnswer, INTERACTION_TYPES, SESSION_TYPES } from "../../../lib/services/copilot";
import useActions from "../hooks/useActions";
import styles from "./styles";
import AgentSwitcher from "./components/AgentSwitcher";
const useStyles = createUseStyles(styles, 'CopilotChat');
var _ref4 = /*#__PURE__*/React.createElement(AgentSwitcher, null);
const CopilotChat = /*#__PURE__*/memo(_ref => {
  var _Object$keys;
  let {
    className,
    locale,
    hashHistory,
    services,
    session,
    interactions = [],
    defaultFirstInteraction,
    disableChatInput // For the Copilot features that doesn't allow user interaction, p.e. vendor_sync_analize
  } = _ref;
  const classes = useStyles();
  const [isLoadingAnswer, setIsLoadingAnswer] = useState(false);
  const [stepComponent, setStepComponent] = useState(null);
  const answerInteractionIdRef = useRef(null);
  const contentRef = useRef(null);
  const questionRef = useRef('');
  const answerRef = useRef(''); // to keep a track of the response
  const cancelSequenceRef = useRef(null); // Ends sequence promise

  const {
    makeAction
  } = useActions({
    hashHistory,
    services
  });
  const {
    setInteraction,
    clearChat,
    chat,
    chatSession,
    setChatSession,
    hasInitialized,
    setHasInitialized,
    setStreamReader
  } = useContext(CopilotContext);
  const getCancelSequence = useCallback(cancel => {
    cancelSequenceRef.current = cancel;
  }, []);
  const cancelSequence = useCallback(() => {
    var _cancelSequenceRef$cu;
    if (cancelSequenceRef !== null && cancelSequenceRef !== void 0 && cancelSequenceRef.current) (_cancelSequenceRef$cu = cancelSequenceRef.current) === null || _cancelSequenceRef$cu === void 0 ? void 0 : _cancelSequenceRef$cu.call(cancelSequenceRef);
    cancelSequenceRef.current = null;
    setStepComponent(null);
    setStreamReader(null);
  }, [setStepComponent, setStreamReader]);
  const onAnswer = useCallback(_ref2 => {
    let {
      id,
      responseId,
      text
    } = _ref2;
    if (text) {
      // To keep track of the previous chunk of text
      answerRef.current = text;
      setInteraction({
        id: answerInteractionIdRef.current,
        responseId,
        text
      });
    }
  }, [setInteraction]);
  const onData = useCallback(data => {
    makeAction({
      actionData: data,
      setStepComponent,
      getCancelSequence
    });
  }, [makeAction, getCancelSequence]);
  const onError = useCallback(parentId => () => {
    setStreamReader(null);
    setInteraction({
      id: answerInteractionIdRef.current,
      isError: true,
      parentId
    });
    answerRef.current = '';
    answerInteractionIdRef.current = null;
  }, [setInteraction, setStreamReader]);
  const onFinish = useCallback(_ref3 => {
    let {
      id,
      text
    } = _ref3;
    answerRef.current = '';
    setIsLoadingAnswer(false);
    setStreamReader(null);
    setInteraction({
      id: answerInteractionIdRef.current,
      text,
      isFinished: true
    });
    answerInteractionIdRef.current = null;
  }, [setInteraction, setStreamReader, setIsLoadingAnswer]);
  const onAction = useCallback(action => {
    setInteraction({
      id: answerInteractionIdRef.current,
      action
    });
  }, [setInteraction]);
  const onSubmitChat = useCallback(suggestion => {
    const question = suggestion || questionRef.current || '';
    cancelSequence();
    setIsLoadingAnswer(true);
    const questionId = uuid();
    answerInteractionIdRef.current = uuid();
    setInteraction({
      text: question,
      id: questionId,
      isQuestion: true
    });
    setInteraction({
      id: answerInteractionIdRef.current,
      text: '',
      isLoading: true,
      parentId: questionId
    });
    return getCopilotStreamAnswer({
      session: _objectSpread(_objectSpread({}, session), {}, {
        id: chatSession === null || chatSession === void 0 ? void 0 : chatSession.id,
        type: !!(chatSession !== null && chatSession !== void 0 && chatSession.id) ? SESSION_TYPES.UPDATE : SESSION_TYPES.CREATE
      }),
      interactions: [...interactions, {
        item: {
          type: INTERACTION_TYPES.MESSAGE,
          content: {
            type: 'input_text',
            text: question
          }
        }
      }],
      setText: onAnswer,
      setData: onData,
      setSession: setChatSession,
      onFinish,
      onError: onError(questionId),
      onAction,
      onSetReader: setStreamReader
    });
  }, [cancelSequence, setInteraction, session, chatSession === null || chatSession === void 0 ? void 0 : chatSession.id, interactions, onAnswer, onData, setChatSession, onFinish, onError, onAction, setStreamReader, setIsLoadingAnswer]);
  const handleClearChat = useCallback(() => {
    cancelSequence();
    clearChat();
  }, [clearChat, cancelSequence]);
  const getContentRef = ref => {
    if (ref) {
      contentRef.current = ref;
      const el = contentRef.current;
      el.scrollTop = el.scrollHeight;
    }
  };
  const getQuestionRef = useCallback(question => {
    questionRef.current = question;
  }, []);
  const isDanaWritting = useMemo(() => {
    return isLoadingAnswer;
  }, [isLoadingAnswer]);
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true);
      if (defaultFirstInteraction) {
        setInteraction({
          id: uuid(),
          text: defaultFirstInteraction,
          isFinished: true,
          isQuestion: false,
          isError: false,
          isLoading: false
        });
      }
      if (interactions.length > 0 && !answerInteractionIdRef.current) {
        setIsLoadingAnswer(true);
        const questionId = uuid();
        answerInteractionIdRef.current = uuid();
        setInteraction({
          id: answerInteractionIdRef.current,
          text: '',
          isLoading: true,
          parentId: questionId
        });
        getCopilotStreamAnswer({
          session,
          interactions,
          setText: onAnswer,
          setData: onData,
          setSession: setChatSession,
          onFinish,
          onError: onError(questionId),
          onAction,
          onSetReader: setStreamReader
        }).finally(() => {
          setIsLoadingAnswer(false);
        });
      }
    }
  }, [setInteraction, defaultFirstInteraction, onAnswer, onData, onFinish, onError, session, interactions, setChatSession, hasInitialized, setHasInitialized, onAction, setStreamReader]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(classes.root, className)
  }, ((_Object$keys = Object.keys(chat)) === null || _Object$keys === void 0 ? void 0 : _Object$keys.length) > 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.chat,
    ref: ref => getContentRef(ref)
  }, /*#__PURE__*/React.createElement(Chat, {
    conversation: chat
  }), stepComponent), !disableChatInput && /*#__PURE__*/React.createElement("div", {
    className: classes.chatBar
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.chatToolbar
  }, _ref4), /*#__PURE__*/React.createElement("div", {
    className: classes.input
  }, /*#__PURE__*/React.createElement(ChatToolbar, {
    getQuestionRef: getQuestionRef,
    onSubmit: onSubmitChat,
    onClear: handleClearChat,
    conversation: chat,
    isLoadingAnswer: isLoadingAnswer,
    isDanaWritting: isDanaWritting,
    locale: locale
  }))));
});
export default CopilotChat;
export { CopilotContext, CopilotProvider, CopilotConsumer };